import {
  emailDoesnTExistMessageDisplayed,
  loginSignupPageClickOnSwitchForm,
  loginSubmit,
  onSwitchContext,
} from '@wix/bi-logger-hls2/v2';
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import React from 'react';
import { ROUTES } from '../routes';
import {
  AUTH_METHODS_BY_MAIL,
  CaptchaStatus,
  EXPERIMENTS,
  FORGOT_PASSWORD_LINK,
  GET_USER_ACCOUNTS_CONTEXT,
  LOGIN_PAGE_CONTEXT,
  SIGNUP_PAGE_CONTEXT,
} from '../utils/constants';
import {
  areAllUserAccountsBlocked,
  ERROR_CODES,
  extractEmailCodeErrorKey,
  extractErrorKeyByErrorCode,
  extractRedirectUrl,
} from '../utils/errorHandler';
import { createTextWithLink } from '../utils/generator';
import {
  Constraint,
  constraints,
  ERROR_TRANSLATION_KEYS,
  generateEmailFieldConstraints,
} from '../utils/validators';
import { CAPTCHA_ACTIONS, PendingCaptcha } from './captcha';
import { LoginFlow } from './loginFlow';
import {
  shouldEnableLoginWithEmailCode,
  shouldRouteBackToLoginInApp,
  shouldShowChangeEmailOnPasswordPage,
} from './constants';
import { FormField } from './formField';
import {
  FlowStart,
  isEmailCodeAvailable,
  isInvalidChallenge,
  startEmailFactorFlow,
} from './emailCodeStore';
import { normalizeEmail } from '../utils/general';
import { RootStore } from './root';
import { SIGNUP_FLOWS } from './signup';
import { HttpError } from '@wix/http-client';
import {
  UserAccountsDataByEmailResponse,
  Values,
  WebAuthnAuthenticationData,
} from '../types';
import { SHOULD_BLOCK_EDITOR_X_SIGNUP_FT } from '../server/constants';
import { PasswordFormField } from './passwordFormField';
import { AUTH_TYPE } from '../components/BlockedAccount/authTypes';
import {
  FactorType,
  StateType,
} from '@wix/ambassador-iam-authentication-v1-authentication/types';
import { PRESETS } from '../utils/presets';
import { createBiLogger } from '../services/biLogger';
import { AuthMethod, setLastUsedAuthMethod } from '../utils/lastUsedAuthMethod';
import {
  lastUsedBiView,
  lastUsedTransactionData,
} from '../utils/lastUsedAuthMethodBi';

const MAP_ERROR_CODE_TO_FIELD_TYPE = {
  [ERROR_CODES.PASSWORD_INCORRECT]: 'password',
  [ERROR_CODES.IAM_PASSWORD_INCORRECT]: 'password',
  [ERROR_CODES.INVALID_EMAIL_OR_PASSWORD]: 'password',
  [ERROR_CODES.USER_DELETED_OR_BLOCKED]: 'email',
  [ERROR_CODES.USER_IS_DELETED]: 'email',
  [ERROR_CODES.USER_IS_BLOCKED]: 'email',
  [ERROR_CODES.LOGIN_NO_SUCH_MAIL]: 'email',
  [ERROR_CODES.LOGIN_NO_SUCH_MAIL_IAM]: 'email',
  [ERROR_CODES.SOCIAL_LOGIN_GET_EMAIL]: 'email',
  [ERROR_CODES.GENERAL_ERROR_CODE]: 'email',
  [ERROR_CODES.RESET_PASSWORD_REQUIRED]: 'email',
  [ERROR_CODES.RESET_PASSWORD_REQUIRED_IAM]: 'email',
  [ERROR_CODES.FACTOR_REQUIRES_RE_ENROLLMENT]: 'email',
  [ERROR_CODES.SSO_LOGIN_MANDATORY_ERROR]: 'email',
  [ERROR_CODES.FACTOR_NOT_FOUND]: 'email',
  [ERROR_CODES.INACTIVE_FACTOR]: 'email',
};

/**
 * The account cannot be logged into with a password, and the way back in is to
 * set one. The legacy login says so outright; IAM says a password factor was
 * not found, which for an account with no password is the same situation.
 */
const PASSWORD_RESET_REQUIRED_ERRORS: string[] = [
  ERROR_CODES.RESET_PASSWORD_REQUIRED,
  ERROR_CODES.RESET_PASSWORD_REQUIRED_IAM,
  ERROR_CODES.FACTOR_REQUIRES_RE_ENROLLMENT,
];

const EMAIL_CODE_REFUSED_ERRORS = [
  ERROR_CODES.EMAIL_NOT_VERIFIED,
  ERROR_CODES.NEW_FLOW_UNIMPLEMENTED_FEATURE,
  ERROR_CODES.UNSUPPORTED_LOGIN_REQUEST,
];

export const LOGIN_FLOWS = {
  FROM_SIGNUP: 'FROM_SIGNUP',
  DEFAULT: 'DEFAULT',
};
export type ILoginFlows = Values<typeof LOGIN_FLOWS>;

export class LoginStore implements AuthStore {
  private readonly rootStore: RootStore;
  private captchaAdded: CaptchaStatus = {
    [LOGIN_PAGE_CONTEXT]: false,
    [SIGNUP_PAGE_CONTEXT]: false,
    [GET_USER_ACCOUNTS_CONTEXT]: false,
  };
  public emailField: FormField;
  public isLoading: boolean = false;
  public passwordField: PasswordFormField;
  private LOGIN_INTERACTION_NAME = 'email-login';
  private loginActionErrorMessage?: string;
  public flow: string = LOGIN_FLOWS.DEFAULT;
  public readonly loginFlow = new LoginFlow();
  // A CAPTCHA a refused code send asked for; the login form renders it.
  public readonly pendingCaptcha = new PendingCaptcha(
    () => this.rootStore.captchaStore,
    (shown, context = LOGIN_PAGE_CONTEXT) => {
      this.captchaAdded[context] = shown;
    },
  );
  // The email the account lookup found eligible for a login code (verified,
  // one Wix account, no password). A code is sent only while the form holds it.
  public codeEligibleEmail?: string = undefined;
  // A code-send error shown on the email field. It must not block the
  // password login it points the user to.
  private hasCodeSendError = false;
  // A code request runs, including the lookup or routing that follows a
  // refused one. It isn't the password login, so Log In doesn't spin for it.
  public isSendingCode = false;
  // The form opened with the account picked in the account selector: its
  // email is changed only back on the email step, through the clear button.
  public isEmailReadOnly = false;
  // The email whose typed password was dropped to pick one of its accounts in
  // the account selector: the picked account's form asks for it again.
  private passwordDroppedForPickOf?: string;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    const { i18n } = this.rootStore;
    const emailRules: Constraint[] = generateEmailFieldConstraints(
      i18n.t.bind(i18n),
    );
    this.emailField = new FormField(
      this.rootStore.userDataStore.email,
      emailRules,
    );
    const fieldRequiredConstraint: Constraint[] = [
      [constraints.required, i18n.t(ERROR_TRANSLATION_KEYS.REQUIRED)],
    ];
    this.passwordField = new PasswordFormField('', fieldRequiredConstraint);

    makeObservable(this, {
      emailField: observable,
      passwordField: observable,
      isLoading: observable,
      titleKey: computed,
      subtitleKey: computed,
      presetSubtitleKey: computed,
      login: action.bound,
      onInitFields: action.bound,
      clear: action.bound,
      onClickForgotPassword: action.bound,
      codeEligibleEmail: observable,
      setCodeEligibleEmail: action.bound,
      endLoginFlow: action.bound,
      isEmailCodeOffered: computed,
      isLoginWithEmailCodeEnabled: computed,
      isSendingCode: observable,
      isEmailReadOnly: observable,
      setEmailReadOnly: action.bound,
      loginWithEmailCode: action.bound,
      onBackClicked: action.bound,
    });
  }

  /**
   * The step the form's back button returns to: the code screen the user left
   * through "Use Your Password" while its code is still open, the account
   * selector that picked its email, else the two-step login's email step. Only
   * where email codes are available. Without one, the button (shown only when
   * opened from signup) goes back in history.
   *
   * The code screen and the selector are the history entry below the form:
   * only "Use Your Password" opens it while a code waits, and only a pick in
   * the selector locks its email.
   */
  get backStep():
    | typeof ROUTES.LOGIN_EMAIL_CODE
    | typeof ROUTES.SELECT_ACCOUNT
    | typeof ROUTES.EMAIL_STEP
    | undefined {
    const { experiments, loginEmailFactorStore, emailStepStore } =
      this.rootStore;
    if (
      !shouldEnableLoginWithEmailCode(experiments) ||
      !isEmailCodeAvailable(this.rootStore)
    ) {
      return undefined;
    }
    if (loginEmailFactorStore?.hasPendingCodeFor(this.emailField.value)) {
      return ROUTES.LOGIN_EMAIL_CODE;
    }
    if (this.isEmailOfPickedAccount) {
      // Its login in progress (an IdP merge) waits there for another pick.
      return ROUTES.SELECT_ACCOUNT;
    }
    if (
      this.flow === LOGIN_FLOWS.DEFAULT &&
      emailStepStore.loginEmailStepStore.showLoginEmailStep
    ) {
      return ROUTES.EMAIL_STEP;
    }
    return undefined;
  }

  get showBackButton(): boolean {
    return this.flow === LOGIN_FLOWS.FROM_SIGNUP || Boolean(this.backStep);
  }

  onBackClicked() {
    if (this.backStep === ROUTES.EMAIL_STEP) {
      // Two-step login: starts over, as the email's own button does, so the
      // next email starts a new login.
      if (shouldShowChangeEmailOnPasswordPage(this.rootStore.experiments)) {
        this.changeEmail();
      } else {
        this.clearEmail();
      }
    } else {
      // Keeps the email: back to the entry below this form. That is the code
      // screen ("Use Your Password" → back), its code still open, so nothing
      // is sent again; the account selector; or signup.
      this.rootStore.navigationStore.goBack();
    }
  }

  /**
   * Login may send the code: email codes are available here
   * (isEmailCodeAvailable) and the account lookup found an eligible email
   * whose account has no password.
   * It doesn't follow edits to the email field.
   */
  get isEmailCodeOffered(): boolean {
    return (
      !!this.codeEligibleEmail &&
      isEmailCodeAvailable(this.rootStore) &&
      shouldEnableLoginWithEmailCode(this.rootStore.experiments)
    );
  }

  /**
   * The code can be requested for the email on the form: only the one the
   * account lookup reported eligible; an IdP login waiting for its first
   * factor runs that lookup too.
   */
  get isLoginWithEmailCodeEnabled(): boolean {
    return (
      this.isEmailCodeOffered && this.isCodeEligibleEmail(this.emailField.value)
    );
  }

  /**
   * Sends a login code to the email on the form and opens the code screen.
   * Resolves false when nothing was sent and the login form should open;
   * true when the user was sent elsewhere or stays where the email was
   * entered.
   */
  async loginWithEmailCode(): Promise<boolean> {
    const { loginEmailFactorStore } = this.rootStore;
    if (!this.isLoginWithEmailCodeEnabled || this.isLoading) {
      return false;
    }
    if (loginEmailFactorStore.isLoading) {
      // A request of the code screen still runs (after browser Back). For
      // another email it's abandoned; for the same one, nothing new is sent
      // and the caller shows the login form.
      if (loginEmailFactorStore.email === this.emailField.value) {
        return false;
      }
      loginEmailFactorStore.reset();
    }
    // Re-run the format rules only: a server error from the previous send
    // would otherwise block every retry until the email is edited.
    this.emailField.triggerValidator();
    if (!this.emailField.isValid) {
      this.emailField.markFieldAsDirty();
      return false;
    }
    this.rootStore.userDataStore.setEmail(this.emailField.value);
    this.isLoading = true;
    this.isSendingCode = true;
    try {
      return await this.requestCodeFromForm();
    } finally {
      runInAction(() => {
        this.isSendingCode = false;
      });
    }
  }

  private async requestCodeFromForm(): Promise<boolean> {
    const { loginEmailFactorStore, navigationStore, captchaStore, i18n } =
      this.rootStore;
    let sendError: string | undefined;
    try {
      sendError = await loginEmailFactorStore.start(this.emailField.value);
    } finally {
      runInAction(() => {
        // Keep the spinner while a completed login redirects.
        if (!loginEmailFactorStore.isCompleting) {
          this.isLoading = false;
        }
      });
    }
    if (!sendError) {
      return true;
    }
    if (
      sendError === ERROR_CODES.LOGIN_NO_SUCH_MAIL ||
      sendError === ERROR_CODES.LOGIN_NO_SUCH_MAIL_IAM
    ) {
      runInAction(() => {
        this.isLoading = true;
      });
      await this.handleNoSuchEmailError();
      return true;
    }
    if (sendError === ERROR_CODES.USER_IS_BLOCKED) {
      navigationStore.navigateToBlockedAccount({
        refferal_info: AUTH_TYPE.EMAIL_AND_PASSWORD,
      });
      return true;
    }
    if (EMAIL_CODE_REFUSED_ERRORS.includes(sendError)) {
      // The backend cannot send this account a code (an unverified email,
      // among others): leave the password as the only way in.
      this.setCodeEligibleEmail(this.emailField.value, false);
      return false;
    }
    if (sendError === ERROR_CODES.THROTTLED) {
      if (this.showErrorOnSignupEmailStep(sendError)) {
        // Asked from signup: the user waits out the limit there.
        return true;
      }
      if (navigationStore.currentRoute === ROUTES.EMAIL_STEP) {
        // The login form opens instead of the code screen: the password is
        // the way in while the limit lasts.
        this.showCodeSendError(
          sendError,
          i18n.t('login.email_code.throttled_use_password', {
            defaultValue:
              'No more resends left. Please try your password instead.',
          }),
        );
        return false;
      }
    }
    if (this.shouldFetchAllAccountAfterFailedLogin(sendError)) {
      // The account logs in another way (SSO, social); route it there.
      // When the lookup finds no other way in, the code just wasn't sent:
      // say so on the email field (not with the password wording).
      runInAction(() => {
        this.isLoading = true;
      });
      const { routed, errorCode } = await this.handleUserWithFailedLogin(
        sendError,
        { showError: false, renderCaptcha: false },
      );
      if (!routed && captchaStore.isCaptchaServerError(errorCode)) {
        // The login form draws the lookup's CAPTCHA (from the email step,
        // once it is mounted), and the next lookup sends its token.
        this.pendingCaptcha.request(errorCode!, GET_USER_ACCOUNTS_CONTEXT);
      } else if (!routed) {
        this.showCodeSendError(ERROR_CODES.GENERAL_ERROR_CODE);
      }
      return routed;
    }
    if (captchaStore.isCaptchaServerError(sendError)) {
      // On the code screen, which renders the CAPTCHA.
      return true;
    }
    // Only a waiting IdP login's token can expire here; that expiry isn't
    // about this code, so it's worded as a general error.
    this.showCodeSendError(
      isInvalidChallenge({ errorCode: sendError })
        ? ERROR_CODES.GENERAL_ERROR_CODE
        : sendError,
    );
    return false;
  }

  /** A code send failed: say so on the email field, where the code was asked for. */
  private showCodeSendError(
    errorCode: string,
    message: string | React.ReactElement = this.generateErrorElement(
      errorCode,
      extractEmailCodeErrorKey(errorCode, 'login'),
    ),
  ) {
    runInAction(() => {
      this.emailField.addError(message);
      this.emailField.markFieldAsDirty();
      this.hasCodeSendError = true;
    });
  }

  /**
   * Shows a code-send error on signup's email step, where the email was
   * entered. False when the send was asked from anywhere else.
   */
  private showErrorOnSignupEmailStep(errorCode: string): boolean {
    const { navigationStore, signupStore, i18n } = this.rootStore;
    if (navigationStore.currentRoute !== ROUTES.SIGNUP_PASSWORD) {
      return false;
    }
    runInAction(() => {
      signupStore.emailField.addError(
        i18n.t(extractEmailCodeErrorKey(errorCode, 'login')),
      );
      signupStore.emailField.markFieldAsDirty();
    });
    return true;
  }

  /**
   * Starts a login with factor EMAIL, which sends the code. With the token of
   * a login waiting for its first factor, it continues that login.
   */
  async requestEmailCode(
    email: string,
    stateToken?: string,
  ): Promise<FlowStart> {
    // A visible CAPTCHA only exists after a refused send, on the code screen
    // or the login form.
    const { currentRoute } = this.rootStore.navigationStore;
    const captchaAdded =
      (currentRoute === ROUTES.LOGIN_EMAIL_CODE ||
        currentRoute === ROUTES.LOGIN_PASSWORD) &&
      this.captchaAdded[LOGIN_PAGE_CONTEXT];
    const recaptchaParams =
      await this.rootStore.captchaStore.handleRecaptchaExecution({
        captchaAdded,
        action: CAPTCHA_ACTIONS.LOGIN,
      });
    const { apiStore } = this.rootStore;
    const start = await startEmailFactorFlow(
      () =>
        apiStore.login(
          apiStore.applyAdditionalLoginParams({
            email,
            password: '',
            rememberMe: true,
            ...recaptchaParams,
            stateToken,
          }),
          FactorType.EMAIL,
        ),
      { pausedOn: ERROR_CODES.LOGIN_FACTOR_REQUIRED, allowMfa: true },
    );
    if (start.kind === 'done' && start.payload?.state !== StateType.SUCCESS) {
      // Any other state asks for a step this flow can't take; there's no session.
      // eslint-disable-next-line no-throw-literal
      throw { errorCode: ERROR_CODES.GENERAL_ERROR_CODE };
    }
    return start;
  }

  get shouldDisplayOneTap() {
    return (
      !this.rootStore.displayStore.isMobile &&
      this.rootStore.displayStore.viewMode !== 'MODAL' &&
      !this.rootStore.displayStore.presetKey &&
      this.rootStore.experiments.enabled(
        EXPERIMENTS.SHOULD_DISPLAY_GOT_ON_SIGNUP_PAGE,
      )
    );
  }

  onNavigateToLogin(
    flow = LOGIN_FLOWS.DEFAULT,
    { isEmailReadOnly = false }: { isEmailReadOnly?: boolean } = {},
  ) {
    this.flow = flow;
    this.setEmailReadOnly(isEmailReadOnly);
    this.onInitFields();
    this.navigateToLoginForm();
    if (isEmailReadOnly) {
      this.askForPasswordDroppedForPick();
    }
  }

  private askForPasswordDroppedForPick() {
    const { userDataStore, notificationStore, i18n } = this.rootStore;
    if (
      this.passwordDroppedForPickOf &&
      this.passwordDroppedForPickOf === normalizeEmail(userDataStore.email)
    ) {
      this.passwordDroppedForPickOf = undefined;
      notificationStore.openWarningToast(
        i18n.t('login.account_selector.reenter_password'),
      );
    }
  }

  /**
   * For an account without a password: sends the code straight away, and
   * opens the login form only when none was sent.
   */
  async onNavigateToLoginWithEmailCode(flow = LOGIN_FLOWS.DEFAULT) {
    this.flow = flow;
    this.setEmailReadOnly(false);
    this.onInitFields();
    if (!(await this.loginWithEmailCode())) {
      this.navigateToLoginForm();
    }
  }

  setEmailReadOnly(isEmailReadOnly: boolean) {
    this.isEmailReadOnly = isEmailReadOnly;
  }

  /**
   * The account selector picked an account for the field's email, and the
   * user hasn't started over since. Also on a history entry that shows that
   * email again.
   */
  get isEmailInPickedOrganization(): boolean {
    return (
      shouldRouteBackToLoginInApp(this.rootStore.experiments) &&
      !!this.rootStore.userDataStore.pickedOrganizationIdFor(
        this.emailField.value,
      )
    );
  }

  /**
   * The form's email is the picked account's: opened from the account
   * selector, or reached through history showing that email again.
   */
  get isEmailOfPickedAccount(): boolean {
    return this.isEmailReadOnly || this.isEmailInPickedOrganization;
  }

  /**
   * The form's email can't be edited. With the Change button, that is any
   * email an earlier step handed to the form (it is changed on the email
   * step); otherwise only the picked account's.
   */
  get isEmailLocked(): boolean {
    if (shouldShowChangeEmailOnPasswordPage(this.rootStore.experiments)) {
      const email = this.emailField.value;
      return (
        !!email &&
        normalizeEmail(email) ===
          normalizeEmail(this.rootStore.userDataStore.email)
      );
    }
    return this.isEmailOfPickedAccount;
  }

  /** The login form, titled as the flow that led to it. */
  navigateToLoginForm() {
    this.rootStore.navigationStore.navigate(
      ROUTES.LOGIN_PASSWORD,
      this.flow === LOGIN_FLOWS.FROM_SIGNUP ? 'Login - from signup' : undefined,
    );
  }

  clear(): void {
    this.emailField.clear();
    this.passwordField.clear();
    this.hasCodeSendError = false;
    this.loginFlow.clear();
    this.captchaAdded = {
      [LOGIN_PAGE_CONTEXT]: false,
      [SIGNUP_PAGE_CONTEXT]: false,
      [GET_USER_ACCOUNTS_CONTEXT]: false,
    };
  }

  clearEmail(): void {
    this.clear();
    this.passwordDroppedForPickOf = undefined;
    this.setEmailReadOnly(false);
    this.setCodeEligibleEmail('', false);
    // Also any email-code challenge for it, including a request still on its
    // way, which would otherwise move a waiting login on again.
    this.rootStore.loginEmailFactorStore?.reset();
    // Going back to the email step means there's no longer a resolved account,
    // so drop its cached social connections. Otherwise the Symphony Facebook
    // gate (socialAuth.showFacebook) would keep reading the previously
    // looked-up account and leave Facebook visible on the email step, unlike a
    // fresh landing.
    this.rootStore.socialAuthStore.setAccountSocialConnectionsProviders([]);
    this.rootStore.navigationStore.navigate(ROUTES.EMAIL_STEP);
  }

  /** The read-only email has a Change button, not a clear button. */
  get showChangeEmail(): boolean {
    return (
      shouldShowChangeEmailOnPasswordPage(this.rootStore.experiments) &&
      this.isEmailLocked
    );
  }

  /**
   * Back to the email step with the email filled in, where the next submit
   * looks it up again. Starts over as clearEmail does.
   */
  changeEmail(): void {
    this.rootStore.emailStepStore.fillInEmail(this.emailField.value);
    this.clearEmail();
  }

  onInitFields(): void {
    this.passwordField.clear();
    this.captchaAdded = {
      [LOGIN_PAGE_CONTEXT]: false,
      [SIGNUP_PAGE_CONTEXT]: false,
      [GET_USER_ACCOUNTS_CONTEXT]: false,
    };
    const { email } = this.rootStore.userDataStore;
    email && this.emailField.setValue(email);
  }

  onClickForgotPassword() {
    this.rootStore.biLogger.report(
      onSwitchContext({
        context: 'forgot-password',
        referrer: this.rootStore.navigationStore.originContext,
      }),
    );
  }

  async handleNoSuchEmailError() {
    const { userDataStore, signupStore, biLogger } = this.rootStore;
    biLogger.report(
      emailDoesnTExistMessageDisplayed({ context: 'email-login' }),
    );
    userDataStore.setEmail(this.emailField.value);
    try {
      // Stays loading until the signup navigation resolves - it can await a
      // verdict first, and `isSubmittable` is what blocks a second submit.
      await signupStore.onNavigateToSignup(SIGNUP_FLOWS.FROM_LOGIN);
    } finally {
      this.isLoading = false;
    }
  }

  get titleKey() {
    const { login } = this.rootStore.displayStore.preset;
    if (this.flow === LOGIN_FLOWS.FROM_SIGNUP) {
      return login?.alreadyHaveAccount?.title?.key ?? 'login.from_signup.title';
    }
    return login?.title?.key ?? 'login.customized.title';
  }

  get subtitleKey() {
    if (
      this.flow === LOGIN_FLOWS.FROM_SIGNUP &&
      this.rootStore.displayStore.preset.id !== PRESETS.WIXEL
    ) {
      return 'login.from_customized_signup.description';
    }
    return undefined;
  }

  get priority() {
    if (
      this.flow === LOGIN_FLOWS.FROM_SIGNUP &&
      this.rootStore.displayStore.preset.id !== PRESETS.WIXEL
    ) {
      return 'secondary';
    }
    return 'primary';
  }

  get presetSubtitleKey() {
    if (this.flow === LOGIN_FLOWS.FROM_SIGNUP) {
      return (
        this.rootStore.displayStore.preset?.login?.alreadyHaveAccount?.subtitle
          ?.key ?? this.rootStore.displayStore.preset?.login?.subtitle?.key
      );
    }
    return this.rootStore.displayStore.preset?.login?.subtitle?.key;
  }

  public setCodeEligibleEmail(email: string, eligible: boolean) {
    this.codeEligibleEmail = eligible ? normalizeEmail(email) : undefined;
  }

  /**
   * IAM answers "no password factor" both for an account that has no password
   * and for one whose factor it withholds — a blocked Wix employee keeps their
   * password but is given no factor. Telling the second group to reset sends
   * them round a loop that cannot unblock them, so the reset message is only
   * for an account the lookup confirmed has no password.
   */
  private isMissingPasswordError(errorCode: string) {
    if (PASSWORD_RESET_REQUIRED_ERRORS.includes(errorCode)) {
      return true;
    }
    // The account the lookup resolved, not the first it returned. Note this
    // path does not see a selector pick: handleUserWithFailedLogin looks the
    // email up again, which clears the resolved account and, for an email with
    // several, resolves none — so a multi-account user returns to the selector
    // instead of reaching this message. See loginFactorNotFound.spec.
    return (
      errorCode === ERROR_CODES.FACTOR_NOT_FOUND &&
      this.rootStore.userDataStore.hasPasswordFor(this.emailField.value) ===
        false
    );
  }

  private isCodeEligibleEmail(email: string) {
    return !!email && this.codeEligibleEmail === normalizeEmail(email);
  }

  /**
   * The login in progress can't be continued (finished, refused, or moved on
   * to MFA with its own token): the next attempt is a plain login, and the
   * form stops presenting it as connecting an IdP.
   */
  public endLoginFlow() {
    this.loginFlow.clear();
    this.rootStore.idpConnectionLoginStore.isIdpFirstFactorFlow = false;
  }

  async executeLogin(
    email: string,
    password: string,
    recaptchaParams,
    webAuthnAuthenticationData?: WebAuthnAuthenticationData,
  ) {
    this.loginActionErrorMessage = undefined;
    // The email path is never badged, but it is half the comparison: these
    // are the people a stale social badge would have pulled away. Attributing
    // it is what makes "did the badge cost the email form anything"
    // answerable.
    const lastUsedData = lastUsedTransactionData(
      this.rootStore.experiments,
      AuthMethod.EMAIL,
    );
    this.rootStore.panorama.startTransaction(
      this.LOGIN_INTERACTION_NAME,
      lastUsedData,
    );
    // A password continues only a login waiting for its first factor; a code
    // challenge it would refuse once expired.
    const stateToken = this.loginFlow.tokenAt(
      email,
      StateType.REQUIRE_FIRST_FACTOR,
    );
    try {
      const data = await this.rootStore.apiStore.login(
        this.rootStore.apiStore.applyAdditionalLoginParams({
          email,
          password,
          rememberMe: true,
          ...recaptchaParams,
          webAuthnAuthenticationData,
          stateToken,
        }),
      );
      this.loginFlow.clear();

      this.rootStore.panorama.finishTransaction(
        this.LOGIN_INTERACTION_NAME,
        lastUsedData,
      );
      return this.completeEmailLogin(email, data?.payload);
    } catch (error: any) {
      const errorCode =
        error.errorCode?.toString() ?? ERROR_CODES.GENERAL_ERROR_CODE;
      this.loginActionErrorMessage = error.message || String(error);
      if (errorCode !== ERROR_CODES.GENERAL_ERROR_CODE) {
        this.rootStore.panorama.finishTransaction(
          this.LOGIN_INTERACTION_NAME,
          lastUsedData,
        );
      }
      if (stateToken && isInvalidChallenge(error)) {
        this.endLoginFlow();
      }
      if (errorCode === ERROR_CODES.SECOND_FACTOR_REQUIRED && error.payload) {
        // The waiting login moved on to MFA, which carries its own token.
        this.endLoginFlow();
        this.rootStore.twoFactorAuthStore.startChallenge(error.payload);
        this.isLoading = false;
        return;
      }
      if (
        errorCode === ERROR_CODES.LOGIN_NO_SUCH_MAIL ||
        errorCode === ERROR_CODES.LOGIN_NO_SUCH_MAIL_IAM
      ) {
        return this.handleNoSuchEmailError();
      }
      if (
        errorCode === ERROR_CODES.SSO_LOGIN_MANDATORY_ERROR ||
        errorCode === ERROR_CODES.FACTOR_NOT_FOUND ||
        errorCode === ERROR_CODES.INACTIVE_FACTOR
      ) {
        const redirectUrl = extractRedirectUrl(error);
        if (redirectUrl) {
          return this.rootStore.navigationStore.redirect(redirectUrl);
        }
      }
      // The user has no password, but he does have an account (or else we would get 'LOGIN_NO_SUCH_MAIL' error),
      // so we fetch more data to know what should be the correct next step
      if (this.shouldFetchAllAccountAfterFailedLogin(errorCode)) {
        throw errorCode;
      }
      this.isLoading = false;
      if (errorCode === ERROR_CODES.USER_IS_BLOCKED) {
        return this.rootStore.navigationStore.navigateToBlockedAccount({
          refferal_info: AUTH_TYPE.EMAIL_AND_PASSWORD,
        });
      }
      throw errorCode;
    }
  }

  /**
   * Resolves once the login is over: true when it went through or moved on
   * (a second factor, a redirect), false when it failed and the error is
   * shown here.
   */
  async login(
    email?: string,
    webAuthnAuthenticationData?: WebAuthnAuthenticationData,
  ): Promise<boolean> {
    if (email !== undefined) {
      this.emailField.setValue(email);
    }

    this.rootStore.biLogger.report(
      loginSubmit({
        remember_me: true,
        view: lastUsedBiView(this.rootStore.experiments, AuthMethod.EMAIL),
      }),
    );
    if (this.hasCodeSendError) {
      // Drop the code-send error; the format rules still apply.
      this.emailField.triggerValidator();
      this.hasCodeSendError = false;
    }
    // A warning belongs to the message that set it, not to the field: left on,
    // the next error on this email — a format error while typing, even — would
    // render as a warning.
    this.emailField.isWarning = false;
    if (this.isEmailLocked) {
      // The email can't be edited, so an error from the last attempt mustn't
      // block a retry; the format rules still apply.
      this.emailField.triggerValidator();
    }
    if (!this.isSubmittable(webAuthnAuthenticationData)) {
      this.markFormAsDirty();
      return false;
    }
    this.isLoading = true;
    if (
      !webAuthnAuthenticationData &&
      (await this.routeEmailNotLookedUp(this.emailField.value))
    ) {
      return true;
    }
    const recaptchaParams =
      await this.rootStore.captchaStore.handleRecaptchaExecution({
        captchaAdded: this.captchaAdded[LOGIN_PAGE_CONTEXT],
        action: CAPTCHA_ACTIONS.LOGIN,
      });
    let loggedIn = false;
    await this.executeLogin(
      this.emailField.value,
      this.passwordField.value,
      recaptchaParams,
      webAuthnAuthenticationData,
    )
      .then(() => {
        loggedIn = true;
      })
      .catch((errorCode) => {
        // Webauthn api error handling
        if (webAuthnAuthenticationData) {
          this.rootStore.webauthnStore.disablePasskeyFlow();
          const passkeyErrorCode =
            errorCode === ERROR_CODES.WEBAUTHN_ASSERTION_FAILED
              ? ERROR_CODES.WEBAUTHN_ASSERTION_FAILED
              : ERROR_CODES.TECHNICAL_ERROR;
          this.rootStore.webauthnStore.reportPasskeyError(
            passkeyErrorCode,
            this.loginActionErrorMessage,
          );
          this.rootStore.notificationStore.openErrorToast(
            this.rootStore.i18n.t(extractErrorKeyByErrorCode(passkeyErrorCode)),
          );
          return;
        }

        if (
          this.shouldFetchAllAccountAfterFailedLogin(errorCode) &&
          !this.isWrongPasswordOfLookedUpEmail(errorCode)
        ) {
          return this.handleUserWithFailedLogin(errorCode);
        }
        this.isLoading = false;
        if (!this.rootStore.captchaStore.isCaptchaServerError(errorCode)) {
          this.rootStore.modalModeHandlerStore.handleErrorReport({
            errorCode,
          });
          // Only a first-factor token can expire here; that expiry isn't
          // about the password, so it's worded as a general error.
          this.addErrorToField(
            isInvalidChallenge({ errorCode })
              ? ERROR_CODES.GENERAL_ERROR_CODE
              : errorCode,
          );
          if (this.isMissingPasswordError(errorCode)) {
            this.emailField.isWarning = true;
          }
        }
        this.captchaAdded[LOGIN_PAGE_CONTEXT] =
          this.rootStore.captchaStore.createOrResetCaptchaIfNeeded(
            errorCode,
            this.captchaAdded[LOGIN_PAGE_CONTEXT],
          );
      })
      .catch((error) => {
        this.rootStore.modalModeHandlerStore.handleErrorReport(error);
      });
    return loggedIn;
  }

  /**
   * An email no lookup resolved yet (the form was opened directly, or its
   * email was edited): looks its accounts up before the login, as the email
   * step does, so the login names the account's organization. Without one,
   * the server logs in the email's personal account, even when it has others.
   *
   * Resolves true when the email was routed elsewhere instead (the account
   * selector, SSO, social login, a login code, the blocked-account page). The
   * typed password is dropped then: it isn't known to be that account's, and a
   * wrong one would count as a failed attempt on it. A failed lookup, or no
   * account, leaves the login as it was.
   */
  private async routeEmailNotLookedUp(email: string): Promise<boolean> {
    const {
      experiments,
      userDataStore,
      captchaStore,
      apiStore,
      emailStepStore,
      navigationStore,
    } = this.rootStore;
    if (
      !shouldRouteBackToLoginInApp(experiments) ||
      userDataStore.organizationIdFor(email) ||
      this.loginFlow.tokenAt(email, StateType.REQUIRE_FIRST_FACTOR)
    ) {
      return false;
    }
    userDataStore.setEmail(email);
    let response: UserAccountsDataByEmailResponse | undefined;
    try {
      const recaptchaParams = await captchaStore.handleRecaptchaExecution({
        captchaAdded: this.captchaAdded[GET_USER_ACCOUNTS_CONTEXT],
        action: CAPTCHA_ACTIONS.GET_USER_ACCOUNTS,
      });
      response = await apiStore.getUserAccounts({ email, ...recaptchaParams });
    } catch (error) {
      const errorCode = (error as HttpError)?.response?.data?.errorCode;
      if (!captchaStore.isCaptchaServerError(errorCode)) {
        return false;
      }
      // Logging in now would skip the account's organization. The next submit
      // looks the email up again, with the solved CAPTCHA's token.
      runInAction(() => {
        this.isLoading = false;
        this.captchaAdded[GET_USER_ACCOUNTS_CONTEXT] =
          captchaStore.renderForRefusedRequest(String(errorCode));
      });
      return true;
    }
    const accounts = response?.accountsData;
    if (!accounts?.length) {
      return false;
    }
    const authType = emailStepStore.getAuthMethodByAccountData(accounts, email);
    if (
      experiments.enabled('specs.login.ShouldNavigateToBlockedAccount') &&
      areAllUserAccountsBlocked(accounts)
    ) {
      runInAction(() => {
        this.passwordField.clear();
        this.isLoading = false;
      });
      navigationStore.navigateToBlockedAccount({
        refferal_info: AUTH_TYPE.EMAIL_AND_PASSWORD,
      });
      return true;
    }
    if (authType.method === AUTH_METHODS_BY_MAIL.LOGIN_PASSWORD) {
      return this.sendCodeToPasswordlessAccount(email, response);
    }
    runInAction(() => {
      this.passwordField.clear();
      this.isLoading = false;
    });
    this.passwordDroppedForPickOf =
      authType.method === AUTH_METHODS_BY_MAIL.MULTI
        ? normalizeEmail(email)
        : undefined;
    await emailStepStore.authHandlers[authType.method]({
      email,
      ...(authType.params ?? {}),
    } as any);
    return true;
  }

  /**
   * The looked-up account has no password but can get a login code: sends it,
   * as the email step does. Resolves false when none was sent, and the login
   * with the typed password goes on as before.
   */
  private async sendCodeToPasswordlessAccount(
    email: string,
    response: UserAccountsDataByEmailResponse,
  ): Promise<boolean> {
    if (
      !this.rootStore.emailStepStore.recordEmailCodeEligibility(email, response)
    ) {
      return false;
    }
    runInAction(() => {
      this.isLoading = false;
    });
    if (await this.loginWithEmailCode()) {
      runInAction(() => this.passwordField.clear());
      return true;
    }
    runInAction(() => {
      this.isLoading = true;
    });
    return false;
  }

  /**
   * A wrong password for an email a lookup already resolved: another lookup
   * would find the same account, so the error is shown without one.
   */
  private isWrongPasswordOfLookedUpEmail(errorCode: string) {
    return (
      shouldRouteBackToLoginInApp(this.rootStore.experiments) &&
      [
        ERROR_CODES.INVALID_EMAIL_OR_PASSWORD,
        ERROR_CODES.PASSWORD_INCORRECT,
        ERROR_CODES.IAM_PASSWORD_INCORRECT,
      ].includes(errorCode) &&
      !!this.rootStore.userDataStore.organizationIdFor(this.emailField.value)
    );
  }

  shouldFetchAllAccountAfterFailedLogin(errorCode: string) {
    const errors = [
      ERROR_CODES.SSO_LOGIN_MANDATORY_ERROR,
      ERROR_CODES.INVALID_EMAIL_OR_PASSWORD,
      ERROR_CODES.PASSWORD_INCORRECT,
      ERROR_CODES.IAM_PASSWORD_INCORRECT,
      ERROR_CODES.FACTOR_NOT_FOUND,
      ERROR_CODES.INACTIVE_FACTOR,
    ];
    return errors.some((e) => e === errorCode);
  }

  async generalHandleUserWithNoPassword({
    captchaAdded,
    email,
    authFlowAdditionalParmas = {},
    originAuthMethod,
    originAuthError,
  }: {
    captchaAdded: boolean;
    email: string;
    authFlowAdditionalParmas?: { [key: string]: any };
    originAuthMethod?: string;
    originAuthError?: string;
  }) {
    const { captchaStore, apiStore, emailStepStore } = this.rootStore;
    const recaptchaParams = await captchaStore.handleRecaptchaExecution({
      captchaAdded,
      action: CAPTCHA_ACTIONS.GET_USER_ACCOUNTS,
    });

    try {
      const response = await apiStore.getUserAccounts({
        email,
        ...recaptchaParams,
      });

      const authType = emailStepStore.getAuthMethodByAccountData(
        response?.accountsData!,
        email,
      );

      const withEmailCode = emailStepStore.recordEmailCodeEligibility(
        email,
        response,
      );
      if (authType.method === originAuthMethod) {
        return { success: false, errorCode: originAuthError };
      }
      const handled = emailStepStore.authHandlers[authType.method]({
        email,
        withEmailCode,
        ...(authType.params ?? {}),
        ...authFlowAdditionalParmas,
      } as any);
      if (withEmailCode) {
        // The code is sent before the caller stops its spinner.
        await handled;
      }
      return { success: true };
    } catch (error) {
      return {
        success: false,
        errorCode: (error as HttpError)?.response?.data.errorCode?.toString(),
      };
    }
  }

  /** An email login (password or code) succeeded: leave for the post-login page. */
  public completeEmailLogin(
    email: string,
    payload?: Parameters<RootStore['navigationStore']['postLogin']>[0],
  ) {
    this.rootStore.tagManagerStore.log('editorx.first.login', {
      email,
    });
    this.rootStore.biLogger = createBiLogger();
    this.rootStore.postLoginStore.biLogger = createBiLogger();
    // Email is never badged (the form is already the default path) — the
    // point of recording it is to retire a social badge the user has since
    // moved away from.
    setLastUsedAuthMethod(AuthMethod.EMAIL);
    return this.rootStore.navigationStore.postLogin(payload ?? {});
  }

  /**
   * Routes the account to the way it logs in. Resolves whether it found one,
   * and the error when it didn't.
   */
  private async handleUserWithFailedLogin(
    loginErrorCode: string,
    {
      showError = true,
      renderCaptcha = true,
    }: { showError?: boolean; renderCaptcha?: boolean } = {},
  ) {
    this.rootStore.userDataStore.setEmail(this.emailField.value);
    const { success, errorCode } = await this.generalHandleUserWithNoPassword({
      captchaAdded: this.captchaAdded[GET_USER_ACCOUNTS_CONTEXT],
      email: this.emailField.value,
      originAuthMethod: AUTH_METHODS_BY_MAIL.LOGIN_PASSWORD,
      originAuthError: loginErrorCode,
    });
    this.isLoading = false;
    if (!success) {
      // A visible-captcha escalation (9981/9982) is not a failure to surface as
      // a field error — it's handled by rendering the captcha below. Showing the
      // message here mapped to the generic "general technical error" (no key for
      // the captcha codes), hiding the fact that the user just needs to solve the
      // "I'm not a robot" challenge.
      if (
        showError &&
        !this.rootStore.captchaStore.isCaptchaServerError(errorCode)
      ) {
        this.addErrorToField(errorCode);
        // This path returns before the login handler's own isWarning branch,
        // so the message would otherwise read as an error where the legacy
        // reset-required code reads as a warning.
        if (this.isMissingPasswordError(errorCode)) {
          this.emailField.isWarning = true;
        }
      }
      if (renderCaptcha) {
        this.captchaAdded[GET_USER_ACCOUNTS_CONTEXT] =
          this.rootStore.captchaStore.createOrResetCaptchaIfNeeded(
            errorCode,
            this.captchaAdded[GET_USER_ACCOUNTS_CONTEXT],
          );
      }
    }
    return { routed: Boolean(success), errorCode };
  }

  public shouldBlockEditorXSignup() {
    const shouldBlockEditorXSignup = this.rootStore.experiments.enabled(
      SHOULD_BLOCK_EDITOR_X_SIGNUP_FT,
    );
    return shouldBlockEditorXSignup && this.rootStore.displayStore.isEditorX;
  }

  private addErrorToField(errorCode: string) {
    const fields = {
      password: this.passwordField,
      email: this.emailField,
    };
    const errorElement = this.generateErrorElement(errorCode);
    const fieldWithError =
      MAP_ERROR_CODE_TO_FIELD_TYPE[errorCode] || 'password';
    fields[fieldWithError]?.addError(errorElement);
  }

  /**
   * FACTOR_NOT_FOUND has no message of its own, so when it means "no password"
   * it borrows the one the legacy login names outright. Only the default: a
   * caller that passed a key of its own keeps it.
   */
  private defaultErrorKey(errorCode: string) {
    return errorCode === ERROR_CODES.FACTOR_NOT_FOUND &&
      this.isMissingPasswordError(errorCode)
      ? 'errors.login.reset_password_required'
      : extractErrorKeyByErrorCode(errorCode, { context: 'password' });
  }

  private generateErrorElement(
    errorCode: string,
    errorKey = this.defaultErrorKey(errorCode),
  ): string | React.ReactElement {
    if (this.isMissingPasswordError(errorCode)) {
      const sessionId = this.rootStore.sessionId.get();
      return createTextWithLink(
        errorKey,
        FORGOT_PASSWORD_LINK(
          sessionId,
          this.rootStore.navigationStore.overrideLocale,
          this.rootStore.userType,
        ),
      );
    }
    return this.rootStore.i18n.t(errorKey);
  }

  public isSubmittable(
    webAuthnAuthenticationData?: WebAuthnAuthenticationData,
  ): boolean {
    if (this.isLoading) {
      return false;
    }
    const hasWebAuthn = !!webAuthnAuthenticationData;
    const hasValidEmailAndPassword =
      this.emailField.isValid && this.passwordField.isValid;
    return hasWebAuthn || hasValidEmailAndPassword;
  }

  private markFormAsDirty() {
    this.emailField.markFieldAsDirty();
    this.passwordField.markFieldAsDirty();
  }

  public reportSwitchToSignup() {
    this.rootStore.biLogger.report(
      loginSignupPageClickOnSwitchForm({
        switch_to: 'signup',
        context: 'login_second_step',
      }),
    );
  }
}
